import React, { useState, useEffect, useContext } from 'react'
import { FormContainer } from 'uiwidgets/FormContainer'
import { FormField } from 'uiwidgets/FormField'
import { Row, Col } from 'react-bootstrap'
import PostalCode from 'uiwidgets/PostalCode'
import CountrySelect from 'uiwidgets/CountrySelect'
import ProvinceSelect from 'uiwidgets/ProvinceSelect'
import { CancelButton } from 'uiwidgets/CancelButton'
import { SubmitButton } from '../../uiwidgets/SubmitButton'
import { BUSINESS_USERS_URL } from 'config/config';
import {
    isEmailAddressValid, isPostalCodeValid,
    isPhoneValid, doesStringHaveSpecialChars,
    containsAlphaNumAndSpaceOnly, validAddress, containsAlphaNumAndSpaceAndPunctuationOnly
} from "../../util/Validators";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getRole } from 'util/Lookup'
import { API } from 'api/API'
import { extractError } from 'api/ErrorUtil'
import { TenantContext } from '../../tenant/TenantContext';

const BusinessUserEdit = () => {

    const initialState = {
        userName: "",
        firstName: "",
        lastName: "",
        companyName: "",
        address: "",
        city: "",
        state: "ON",
        zip: "",
        mobilePhoneNumber: "",
        officePhoneNumber: "",
        email: "",
        country: "CA",
        readOnly: false,
        role: "",
        errors: {}
    }

    const initialValidationState = {
        userNameError: false,
        firstNameError: false,
        lastNameError: false,
        addressError: false,
        cityError: false,
        stateError: false,
        zipError: false,
        mobilePhoneNumberError: false,
        officePhoneNumberError: false,
        emailError: false,
        countryError: false,
        companyNameError: false,
        roleError: false,

        userNameErrMsg: "",
        firstNameErrMsg: "",
        lastNameErrMsg: "",
        companyNameErrMsg: "",
        addressErrMsg: "",
        cityErrMsg: "",
        stateErrMsg: "",
        zipErrMsg: "",
        mobilePhoneNumberErrMsg: "",
        officePhoneNumberErrMsg: "",
        emailErrMsg: "",
        countryErrMsg: "",
        roleErrMsg: "",
        isFormValid: true
    }

    const requestParams = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [formValidityData, setFormValidityData] = useState(initialValidationState);
    const [editMode, setEditMode] = useState('add');
    const { businessConfig } = useContext(TenantContext);

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    useEffect(() => {
        if (requestParams.userId && requestParams.userId !== 'newuser') {
            if (location.state?.userDetail) {
                const user = location.state.userDetail.user;
                const userRole = location.state.userDetail.userRole;
                setFormData({
                    ...user,
                    role: userRole.role
                });
                setEditMode("edit");
            }
        }
    }, [])

    const handleCountryChange = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let defaultState = "ON";
        if (value === "US") {
            defaultState = "MI";
        }
        setFormData({
            ...formData,
            [name]: value,
            state: defaultState
        });
    }

    const handleChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        setFormData({
            ...formData,
            [name]: value
        })
    }

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        let isFormValid = true;
        const tagNames = ['select', 'input', 'textarea'];

        for (let tagName of tagNames) {
            //validate full form
            for (let i = 0; i < event.target.getElementsByTagName(tagName).length; i++) {
                let fieldName = event.target.getElementsByTagName(tagName)[i].name;
                let fieldValue = event.target.getElementsByTagName(tagName)[i].value;
                let { hasError, error } = validateInput(fieldName, fieldValue);
                setFormValidityData({
                    ...formValidityData,
                    [fieldName + 'Error']: hasError,
                    [fieldName + 'ErrMsg']: error
                })
                if (hasError) {
                    isFormValid = false;
                    alert("Form has validation error")
                    return false;
                }
            }
        }

        if (!isFormValid) {
            alert("The form still has validation errors!");
            return false;
        }
        setFormData({ ...formData, readOnly: true });
        try {
            //save the data
            if (editMode === 'edit') {
                await API.put(BUSINESS_USERS_URL + "/" + formData.id, formData)
                setFormData({
                    ...formData,
                    readOnly: false,
                    infoMessage: "User was updated successfully"
                })
            } else if (editMode === 'add') {
                await API.post(BUSINESS_USERS_URL, formData)
                setFormData({
                    ...formData,
                    readOnly: true,
                    infoMessage: "User was created successfully"
                })
            } else {
                setFormData({
                    ...formData,
                    readOnly: false,
                    errors: {
                        messaaage: "Could not determine edit mode. please contact support"
                    }
                }
                )
            }
        } catch (exception) {
            let errors = extractError(exception);
            setFormData({
                ...formData,
                errors: errors
            })
        }
    }

    const handleCancel = (event) => {
        navigate("/businessusers");
    }

    const validateInput = (name, value) => {
        let hasError = false,
            error = ""
        switch (name) {
            case "userName":
                if (value.trim() === "") {
                    hasError = true
                    error = "User name cannot be empty"
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Username can contain only alpha-numeric characters"
                } else if (value.length < 5) {
                    hasError = true
                    error = "Username must be contain more than 5 characters"
                }
                else {
                    hasError = false
                    error = ""
                }
                break
            case "role":
                if (value.trim() === "") {
                    hasError = true
                    error = "Select a valid role"
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Role can only contain alpha numeric characters";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "firstName":
                if (value.trim() === "") {
                    hasError = true
                    error = "First name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid First Name. Only alphbets are allowed";
                }
                else if (doesStringHaveSpecialChars(value)) {
                    hasError = true
                    error = "Invalid First Name. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "lastName":
                if (value.trim() === "") {
                    hasError = true
                    error = "Last name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid Last Name. Only alphbets are allowed";
                } else if (doesStringHaveSpecialChars(value)) {
                    hasError = true
                    error = "Invalid Last Name. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "companyName":
                if (value.trim() === "") {
                    hasError = false
                    error = ""
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Invalid Company Name. Only alpha-numeric characters are allowed"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "address":
                if (value.trim() === "") {
                    hasError = true
                    error = "Address name cannot be empty"
                } else if (!validAddress(value)) {
                    hasError = true
                    error = "Invalid Address"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "city":
                if (value.trim() === "") {
                    hasError = true
                    error = "City name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "City name can only contain alpha numeric characters";
                } else if (doesStringHaveSpecialChars(value)) {
                    hasError = true
                    error = "Invalid City. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "state":
                if (value.trim() === "") {
                    hasError = true
                    error = "State name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "State can only contain alpha numeric characters";
                } else if (doesStringHaveSpecialChars(value)) {
                    hasError = true
                    error = "Invalid State. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "country":
                if (value.trim() === "") {
                    hasError = true
                    error = "Country name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Country name can only contain alpha numeric characters";
                } else if (doesStringHaveSpecialChars(value)) {
                    hasError = true
                    error = "Invalid Country. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "email":
                if (value.trim() === "") {
                    hasError = true
                    error = "Email cannot be empty"
                } else if (!isEmailAddressValid(value)) {
                    hasError = true
                    error = "Invalid Email"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "mobilePhoneNumber":
                if (value.trim() === "") {
                    hasError = true
                    error = "Mobile cannot be empty"
                } else if (!isPhoneValid(value)) {
                    hasError = true
                    error = "Invalid Mobile Number. Use 10 digits only"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "officePhoneNumber":
                if (value.trim() === "") {
                    hasError = false;
                    error = "";
                } else if (!isPhoneValid(value)) {
                    hasError = true
                    error = "Invalid Office Number. Use 10 digits only"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "zip":
                if (value.trim() === "") {
                    hasError = true
                    error = "Postal Code cannot be empty"
                } else if (!isPostalCodeValid(value, formData.country)) {
                    hasError = true
                    error = "Invalid Postal Code"
                } else {
                    hasError = false
                    error = ""
                }
                break
            default:
                break
        }
        return { hasError, error }
    }
    return (
        <FormContainer title="User Details" submitButtonFunction={handleSubmit}
            errors={formData.errors} infoMessage={formData.infoMessage} toastCloseFunc={onToastClose} >
            <Row>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="userName" label="User Name" type="text" value={formData.userName}
                        placeholder="User Name" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.userNameError} valMsg={formValidityData.userNameErrMsg}
                        readOnly={editMode === 'edit' || formData.readOnly} />
                </Col>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="email" label="Email" type="text" value={formData.email}
                        placeholder="Email" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.emailError} valMsg={formValidityData.emailErrMsg}
                        readOnly={editMode === 'edit' || formData.readOnly} />
                </Col>
                <Col>
                    <FormField name="role" label="Role" type="select" value={formData.role}
                        placeholder="Role" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.roleError} valMsg={formValidityData.roleErrMsg}>
                        <option value="">Select Role</option>
                        <option value="ROLE_BUSINESS_ADMIN">{getRole("ROLE_BUSINESS_ADMIN")}</option>
                        {(businessConfig.appFeatures.includes('DONATION_MANAGEMENT')) &&
                        <option value="ROLE_DONATION_BOX">{getRole("ROLE_DONATION_BOX")}</option>}
                    </FormField>
                </Col>
            </Row>
            <Row>
                <Col mb={6} lg={6} sm={12}>
                    <FormField name="firstName" label="First Name" type="text" value={formData.firstName}
                        placeholder="First Name" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.firstNameError} valMsg={formValidityData.firstNameErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
                <Col mb={6} lg={6} sm={12}>
                    <FormField name="lastName" label="Last Name" type="text" value={formData.lastName}
                        placeholder="Last Name" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.lastNameError} valMsg={formValidityData.lastNameErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
            </Row>
            <Row>
                <Col mb={7} lg={7} sm={6}>
                    <FormField name="companyName" label="Company Name" type="text" value={formData.companyName}
                        placeholder="Company Name" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.companyNameError} valMsg={formValidityData.companyNameErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
            </Row>
            <Row>
                <Col mb={12} lg={12} sm={12}>
                    <FormField name="address" label="Address" type="text" value={formData.address}
                        placeholder="Address" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.addressError} valMsg={formValidityData.addressErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
            </Row>
            <Row>
                <Col mb={2} lg={2} sm={6}>
                    <CountrySelect name="country" onChange={handleCountryChange} onBlur={handleValidation}
                        valstate={!formValidityData.countryError} valMsg={formValidityData.countryErrMsg}
                        readOnly={formData.readOnly} value={formData.country} />
                </Col>
                <Col mb={4} lg={4} sm={6}>
                    <ProvinceSelect name="state" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.stateError} valMsg={formValidityData.stateErrMsg}
                        readOnly={formData.readOnly} value={formData.state} country={formData.country} />
                </Col>
            </Row>
            <Row>
                <Col mb={8} lg={8} sm={12}>
                    <FormField name="city" label="City" type="text" value={formData.city}
                        placeholder="City" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.cityError} valMsg={formValidityData.cityErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
                <Col mb={4} lg={4} sm={6}>
                    <PostalCode name="zip" label="Postal Code" type="text" value={formData.zip}
                        placeholder="Postal Code" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.zipError} valMsg={formValidityData.zipErrMsg}
                        readOnly={formData.readOnly} country={formData.country} />
                </Col>
            </Row>

            <Row>
                <Col mb={6} lg={6} sm={12}>
                    <FormField name="mobilePhoneNumber" label="Mobile Phone Number" type="text" value={formData.mobilePhoneNumber}
                        placeholder="Mobile Phone Number" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.mobilePhoneNumberError} valMsg={formValidityData.mobilePhoneNumberErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
                <Col mb={6} lg={6} sm={12}>
                    <FormField name="officePhoneNumber" label="Office Phone Number" type="text" value={formData.officePhoneNumber}
                        placeholder="Office Phone Number" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.officePhoneNumberError} valMsg={formValidityData.officePhoneNumberErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
            </Row>
            <Row><Col>
                <SubmitButton label='Save User' disabled={formData.readOnly} className="me-4" />
                <CancelButton label='Cancel' disabled={formData.readOnly} onClick={handleCancel} />
            </Col>
            </Row>
        </FormContainer>
    )
}

export default BusinessUserEdit